import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

import { ExtractHeadlessButtonProps } from '@/common/buttons';
import { Link } from '@/common/components/link';
import { ExtractLinkProps, isLinkProps } from '@/common/links';
import { SpinnerThirdIcon } from '@/icons/fa/regular';

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold cursor-pointer',
    // Sizing
    'px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6',
    // Focus
    'focus:outline-hidden data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
    // Disabled
    'data-disabled:opacity-50',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]'
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-(--btn-border)',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-(--btn-bg)',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow-sm',
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
    // Inner highlight shadow
    'after:shadow-[shadow:inset_0_1px_--theme(--color-white/15%)]',
    // White overlay on hover
    'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',
    // Dark mode: `after` layer expands to cover entire button
    'dark:after:-inset-px dark:after:rounded-lg',
    // Disabled
    'data-disabled:before:shadow-none data-disabled:after:shadow-none'
  ],
  outline: [
    // Base
    'border-zinc-950/10 text-primary-800 data-active:bg-primary-800/[2.5%] data-hover:bg-primary-800/[2.5%]',
    // Dark mode
    'dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5',
    // Icon
    '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]'
  ],
  plain: [
    // Base
    'border-transparent text-primary-800 data-active:bg-primary-800/5 data-hover:bg-primary-800/5',
    // Dark mode
    'dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10',
    // Icon
    '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]'
  ],
  colors: {
    primary: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-primary-800)] [--btn-border:var(--color-primary-900)]/90',
      '[--btn-icon:var(--color-white)] data-active:[--btn-icon:var(--color-primary-800/90)] data-hover:[--btn-icon:var(--color-primary-800/90)]'
    ],
    danger: [
      'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-700)] [--btn-border:var(--color-red-800)]/90',
      '[--btn-icon:var(--color-red-400)] data-active:[--btn-icon:var(--color-red-300)] data-hover:[--btn-icon:var(--color-red-300)]'
    ]
  }
};

type ButtonProps = (
  { color?: keyof typeof styles.colors; outline?: never; plain?: never } |
  { color?: never; outline: true; plain?: never } |
  { color?: never; outline?: never; plain: true }
) & { className?: string; children: React.ReactNode; disabled?: boolean; loading?: boolean; loadingIndicator?: React.ReactNode } & (
  | ExtractHeadlessButtonProps
  | ExtractLinkProps
  );

export const Button = forwardRef(function Button(
  { color, outline, plain, className, children, loading, loadingIndicator = <LoadingIndicator />, disabled, ...rest }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = clsx(
    className,
    styles.base,
    outline ? styles.outline : plain ? styles.plain : clsx(styles.solid, styles.colors[color ?? 'primary'])
  );

  const props = rest as ExtractHeadlessButtonProps | ExtractLinkProps;

  return isLinkProps(props) ?
      (
        <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>} aria-disabled={disabled || loading}>
          <TouchTarget>
            {loading && loadingIndicator}
            {children}
          </TouchTarget>
        </Link>
      ) :
      (
        <Headless.Button {...props} className={clsx(classes, 'cursor-default')} ref={ref} disabled={disabled || loading} aria-disabled={disabled || loading}>
          <TouchTarget>
            {loading && loadingIndicator}
            {children}
          </TouchTarget>
        </Headless.Button>
      );
});

function LoadingIndicator() {
  return <SpinnerThirdIcon className="size-4 animate-spin" />;
}

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}

type ButtonGroupProps = {
  align?: 'start' | 'end';
  /**
   * True if the button group should lay out vertically (flex-col), false to lay
   * out horizontally (flex-row).
   */
  vertical?: boolean;
} & React.ComponentPropsWithoutRef<'div'>;

/**
 * A flex group of <Button/> components.
 */
export function ButtonGroup({ className, align = 'end', vertical = false, ...props }: ButtonGroupProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex mt-4 items-center gap-3 *:w-full',
        align == 'start' ? 'justify-start' : '',
        align == 'end' ? 'justify-end' : '',
        vertical ? 'flex-col' : 'flex-col-reverse sm:flex-row sm:*:w-auto'
      )}
    />
  );
}

export function ButtonGroupSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, 'flex-1')}
    />
  );
}
